import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { currencyCalculatorData } from './data/currencies';
import * as classes from './CurrencyCalculator.module.scss';
import axios from 'axios';
import Loader from '../loader/Loader';
import PropTypes from 'prop-types';
import { convertNumberToCurrency } from '../../utils';

import downloadNavy from './assets/download-navy.svg';
import refresh from './assets/refresh.svg';

import AUD from '../../../content/media-library/flags/AUD.jpg';
import CAD from '../../../content/media-library/flags/CAD.jpg';
import CHF from '../../../content/media-library/flags/CHF.jpg';
import CZK from '../../../content/media-library/flags/CZK.jpg';
import DKK from '../../../content/media-library/flags/DKK.jpg';
import EUR from '../../../content/media-library/flags/EUR.jpg';
import GBP from '../../../content/media-library/flags/GBP.jpg';
import HKD from '../../../content/media-library/flags/HKD.jpg';
import HUF from '../../../content/media-library/flags/HUF.jpg';
import NOK from '../../../content/media-library/flags/NOK.jpg';
import NZD from '../../../content/media-library/flags/NZD.jpg';
import PLN from '../../../content/media-library/flags/PLN.jpg';
import RON from '../../../content/media-library/flags/RON.jpg';
import SEK from '../../../content/media-library/flags/SEK.jpg';
import SGD from '../../../content/media-library/flags/SGD.jpg';
import TRY from '../../../content/media-library/flags/TRY.jpg';
import USD from '../../../content/media-library/flags/USD.jpg';
import ZAR from '../../../content/media-library/flags/ZAR.jpg';

const flags = {
  AUD,
  CAD,
  CHF,
  CZK,
  DKK,
  EUR,
  HKD,
  HUF,
  NZD,
  NOK,
  PLN,
  RON,
  SEK,
  SGD,
  TRY,
  USD,
  ZAR
};

const CurrencyCalculator = ({ currency }) => {
  const [targetCurrency, setTargetCurrency] = useState(
    currencyCalculatorData.find((data) => data.currency === currency)
  );
  const [forwardRate, setForwardRate] = useState(0);
  const [inverseRate, setInverseRate] = useState(0);
  const [deliveryType, setDeliveryType] = useState('swift');
  const [entryAmount, setEntryAmount] = useState(1000);
  const [totalReceived, setTotalReceived] = useState(0);
  const [showAllCurrencies, setShowAllCurrencies] = useState(false);
  const [forwards, setForwards] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  const deliveryFee = useMemo(() => {
    return targetCurrency[deliveryType];
  }, [deliveryType, targetCurrency]);

  const starlingFeeRate = 0.004;
  const starlingInverseFeeRate = 1 - starlingFeeRate;
  const getStarlingFee = (x) => x * starlingFeeRate;

  const starlingFee = getStarlingFee(entryAmount);
  const conversionAmount = entryAmount - starlingFee - deliveryFee;

  const calculate = useCallback(
    (value) => {
      if (value < 10 || value > 10000) {
        setTotalReceived(0);
      } else {
        const newAmount = value - deliveryFee;
        const starlingFee = getStarlingFee(value);
        const deductedAmount = newAmount - starlingFee;

        setTotalReceived((deductedAmount * forwardRate).toFixed(2));
      }
    },
    [deliveryFee, forwardRate]
  );

  const calculateInverse = useCallback(
    (value) => {
      const deductedAmount = value * inverseRate;
      const starlingFee = getStarlingFee(deductedAmount + deliveryFee) / starlingInverseFeeRate;

      setEntryAmount(Number(deductedAmount + starlingFee + deliveryFee).toFixed(2));
    },
    [deliveryFee, inverseRate, starlingInverseFeeRate]
  );

  const fetchRate = useCallback(() => {
    setIsLoading(true);

    axios
      .get(
        `${window.starlingbank.getConfig('currencyCalculatorUrl')}?sourceCurrency=GBP&targetCurrency=${
          targetCurrency.currency
        }`
      )
      .then(({ data }) => {
        setForwardRate(data.forward.rate);
        setInverseRate(data.inverse.rate);
        setIsLoading(false);
      })
      .catch((err) => {});
    targetCurrency.local === undefined && setDeliveryType('swift');
  }, [targetCurrency]);

  useEffect(() => {
    fetchRate();
  }, [targetCurrency, fetchRate]);

  useEffect(() => {
    if (forwards) {
      calculate(entryAmount);
    } else {
      calculateInverse(totalReceived);
    }
  }, [calculate, calculateInverse, entryAmount, forwards, totalReceived]);

  const validAmount = useMemo(() => 10 <= entryAmount && entryAmount <= 10000, [entryAmount]);

  return (
    <form className={classes.currencyCalculator}>
      <h2 id="sending">You send</h2>
      <div className={classes.currencyCalculator_currencyInput}>
        <div className={classes.currencyCalculator_currencyLabel}>
          <span>GBP</span>
        </div>
        <input
          type="number"
          step="any"
          min="10"
          max="10000"
          value={entryAmount}
          onChange={(event) => {
            const value = event.target.value;
            setForwards(true);
            setEntryAmount(value);
          }}
          disabled={isLoading && 'disabled'}
          aria-labelledby="sending"
        />
        <div>
          <div
            className={`${classes.currencyCalculator_flagIconWrapper} ${
              isLoading && classes.currencyCalculator_disabled
            }`}
          >
            <img src={GBP} alt="Great Britain Flag" />
          </div>
        </div>
      </div>
      <h2 id="receiving">They receive</h2>
      <div
        className={`${classes.currencyCalculator_currencyInput} ${
          showAllCurrencies ? classes.remove_borderRadius : ''
        } `}
      >
        <div className={`${classes.currencyCalculator_currencyLabel}`}>
          <span>{targetCurrency.currency}</span>{' '}
          <button onClick={() => setShowAllCurrencies(!showAllCurrencies)} type="button">
            <img
              id="dropdown-arrow"
              src={downloadNavy}
              alt={`Select this button to ${showAllCurrencies ? 'close' : 'open'} the list of available countries`}
              className={
                showAllCurrencies ? classes.currencyCalculator_arrow__rotate : classes.currencyCalculator_arrow
              }
            />
          </button>
        </div>
        <input
          type="number"
          step="any"
          value={!isLoading ? totalReceived : ''}
          min="0"
          onChange={(event) => {
            const value = event.target.value;
            setForwards(false);
            setTotalReceived(value);
          }}
          disabled={isLoading && 'disabled'}
          aria-labelledby="receiving"
        />
        <div
          className={`${classes.currencyCalculator_flagIconWrapper} ${
            isLoading && classes.currencyCalculator_disabled
          }`}
        >
          <img src={flags[targetCurrency.currency]} alt={`${targetCurrency.name} flag`} />
        </div>
      </div>
      <div className={classes.currencyCalculator_wrapper}>
        {showAllCurrencies && (
          <ul className={classes.currencyCalculator_currencyList}>
            {currencyCalculatorData.map((country) => {
              return (
                <li
                  key={country.name}
                  className={targetCurrency === country ? classes.currencyCalculator_selected : ''}
                >
                  <button
                    onClick={() => {
                      setTargetCurrency(country);
                      setShowAllCurrencies(false);
                    }}
                  >
                    <p>
                      {country.currency} <span>{country.name}</span>
                    </p>
                    <div>
                      <img src={flags[country.currency]} alt={`${country.name} flag`} />
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <fieldset className={classes.currencyCalculator_types}>
        <legend id="paymentMethodLabel" className="screen-reader-text">
          Payment method:
        </legend>
        <div role="radiogroup" aria-labelledby="paymentMethodLabel">
          <input
            type="radio"
            id="swift"
            name="type"
            value="swift"
            checked={(deliveryType === 'swift' && 'checked') || (!targetCurrency.local && 'checked')}
            onChange={(event) => {
              setDeliveryType(event.target.value);
            }}
          />{' '}
          <label htmlFor="swift">SWIFT payment</label>
          {deliveryType === 'swift' && (
            <ul>
              <li>- Sent from you with your reference</li>
              <li>- Proof of payment available</li>
              <li>- Recommended when paying a bill</li>
            </ul>
          )}
        </div>
        <div>
          <input
            type="radio"
            id="local"
            name="type"
            value="local"
            checked={deliveryType === 'local' && 'checked'}
            onChange={(event) => {
              setDeliveryType(event.target.value);
            }}
            disabled={targetCurrency.local === undefined}
          />{' '}
          <label htmlFor="local">Low cost payment</label>
          {deliveryType === 'local' && (
            <ul>
              <li>- Sent from our partner without a reference</li>
              <li>- Not possible to recall if a mistake is made</li>
            </ul>
          )}
        </div>
      </fieldset>

      <div className={classes.currencyCalculator_wrapper}>
        <div className={classes.currencyCalculator_summary}>
          {!validAmount && (
            <div className={classes.currencyCalculator_overlay}>
              <p className={classes.currencyCalculator_invalid_amount}>
                You must enter a value between £10 and £10,000.
              </p>
            </div>
          )}
          {isLoading && (
            <div className={classes.currencyCalculator_overlay}>
              <Loader />
            </div>
          )}
          <div className={classes.currencyCalculator_breakdown}>
            <h3>Summary:</h3>
            <ul>
              <li className={classes.currencyCalculator_summary_section}>
                <span>0.4% transfer fee</span>

                <span>{!isLoading && validAmount ? convertNumberToCurrency(starlingFee, 'en-GB', 'GBP') : '-'}</span>
              </li>
              <li className={classes.currencyCalculator_summary_section}>
                <span>Delivery fee</span>
                <span>
                  {!isLoading
                    ? convertNumberToCurrency(
                        deliveryType === 'swift' ? targetCurrency.swift : targetCurrency.local,
                        'en-GB',
                        'GBP'
                      )
                    : '-'}
                </span>
              </li>
              <li className={classes.currencyCalculator_summary_section}>
                <span>Conversion amount</span>
                <span>
                  {!isLoading && validAmount ? convertNumberToCurrency(conversionAmount, 'en-GB', 'GBP') : '-'}
                </span>
              </li>
              <li className={classes.currencyCalculator_summary_section}>
                <span className={classes.currencyCalculator_summary_exchangeRate}>
                  <span>Exchange rate</span>
                  <button onClick={fetchRate} type="button">
                    <img alt="Select this button to refresh the rate" src={refresh} />
                  </button>
                </span>
                <span>{!isLoading && validAmount ? forwardRate : '-'}</span>
              </li>
              <li>
                <span>Total received:</span>
                <span>{!isLoading && validAmount ? `${targetCurrency.symbol}${totalReceived}` : '-'}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </form>
  );
};

CurrencyCalculator.propTypes = {
  currency: PropTypes.oneOf([
    'AUD',
    'CAD',
    'CHF',
    'CZK',
    'DKK',
    'EUR',
    'HKD',
    'HUF',
    'NZD',
    'NOK',
    'PLN',
    'RON',
    'SEK',
    'SGD',
    'TRY',
    'USD',
    'ZAR'
  ])
};

export default CurrencyCalculator;
